// src/services/discoveryService.js
import { collection, doc, getDoc, getDocs, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { DISCOVERY_XP, RESTORATION_XP, TEAM_BONUS_XP, levelForXp } from '../utils/levels';

const discoveriesRef = (visitorId) => collection(db, 'visitors', visitorId, 'discoveries');

export const getVisitorDiscoveries = async (visitorId) => {
  if (!visitorId) return [];
  const snap = await getDocs(discoveriesRef(visitorId));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
};

export const grantXp = async (visitorId, amount) => {
  if (!visitorId || !amount) return null;
  const visitorRef = doc(db, 'visitors', visitorId);
  const snap = await getDoc(visitorRef);
  const prevXp = Number(snap.exists() ? snap.data()?.xp || 0 : 0);
  const nextXp = prevXp + amount;
  await setDoc(visitorRef, { xp: nextXp }, { merge: true });
  const prevLevel = levelForXp(prevXp);
  const nextLevel = levelForXp(nextXp);
  return { prevXp, nextXp, gained: amount, prevLevel, nextLevel, leveledUp: nextLevel > prevLevel };
};

export const recordDiscovery = async (visitorId, placement, xpMultiplier = 1) => {
  if (!visitorId || !placement?.id) return { isNew: false, xp: null };
  const ref = doc(discoveriesRef(visitorId), placement.id);
  const existing = await getDocs(discoveriesRef(visitorId));
  const alreadyThere = existing.docs.some((d) => d.id === placement.id);

  const payload = {
    placementId: placement.id,
    sceneId: placement.sceneId || null,
    modelId: placement.modelId || null,
    title: placement.title || placement.modelName || 'Escultura',
    artist: placement.artist || '',
    thumbnailUrl: placement.thumbnailUrl || '',
    lastSeenAt: serverTimestamp()
  };
  if (!alreadyThere) {
    payload.favorite = false;
    payload.discoveredAt = serverTimestamp();
  }

  await setDoc(ref, payload, { merge: true });

  const xp = alreadyThere
    ? null
    : await grantXp(visitorId, Math.round(DISCOVERY_XP * (Number(xpMultiplier) || 1)));

  return { isNew: !alreadyThere, xp };
};

export const setDiscoveryFavorite = async (visitorId, placementId, favorite) => {
  if (!visitorId || !placementId) return;
  await setDoc(doc(discoveriesRef(visitorId), placementId), { favorite }, { merge: true });
};

export const markRestored = async (visitorId, placementId, { xpMultiplier = 1, teamBonus = false } = {}) => {
  if (!visitorId || !placementId) return { alreadyRestored: false, xp: null };
  const ref = doc(discoveriesRef(visitorId), placementId);
  const snap = await getDoc(ref);
  if (snap.exists() && snap.data()?.restored) {
    return { alreadyRestored: true, xp: null };
  }
  await setDoc(ref, { restored: true, restoredAt: serverTimestamp() }, { merge: true });
  const baseXp = Math.round(RESTORATION_XP * (Number(xpMultiplier) || 1));
  const totalXp = baseXp + (teamBonus ? TEAM_BONUS_XP : 0);
  const xp = await grantXp(visitorId, totalXp);
  return { alreadyRestored: false, xp, teamBonus };
};
